import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FlaskConical, Info } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useBetaProgram } from '../../beta/hooks';
import { stripBetaPrefix } from '../../beta/routing';

const BetaModeBanner = () => {
  const [showDetails, setShowDetails] = useState(false);
  const location = useLocation();
  const { isBetaMode, program } = useBetaProgram();

  if (!isBetaMode || !program) {
    return null;
  }

  const differences = program.differences || [];
  const strippedPath = stripBetaPrefix(location.pathname);
  const betaOnlyPaths = [
    '/samples',
    '/email-automation',
    '/email-generator',
    '/features',
    '/seo-lab',
    '/mixes-catalog',
    '/admin',
    '/document-flow',
    '/production-calendar'
  ];
  const liveRoutePath = betaOnlyPaths.includes(strippedPath) ? '/' : strippedPath;

  return (
    <div className="bg-amber-100 border-y-2 border-amber-400">
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <p className="text-sm text-amber-900 flex items-center gap-2">
            <FlaskConical size={16} />
            <span>
              You are viewing beta preview:
              {' '}
              <strong>{program.name}</strong>
            </span>
          </p>
          <div className="flex items-center gap-3 text-sm flex-wrap">
            <button
              type="button"
              onClick={() => setShowDetails((current) => !current)}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-amber-700 rounded text-amber-900 hover:bg-amber-200 transition-colors font-medium"
            >
              <Info size={14} />
              What Is Different?
              {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            <Link to="/access" className="text-amber-900 hover:text-amber-950 underline font-medium">
              Secure Portal
            </Link>
            <Link to={liveRoutePath} className="text-amber-900 hover:text-amber-950 underline font-medium">
              View Live Route
            </Link>
          </div>
        </div>

        {showDetails && (
          <div className="mt-3 bg-amber-50 border border-amber-300 rounded-lg p-4">
            <p className="text-sm text-amber-900 mb-2">
              <strong>Intention:</strong>
              {' '}
              {program.intention || program.description}
            </p>
            {differences.length > 0 && (
              <ul className="space-y-1 text-sm text-amber-900">
                {differences.map((difference) => (
                  <li key={difference} className="flex items-start gap-2">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-700" />
                    <span>{difference}</span>
                  </li>
                ))}
              </ul>
            )}
            {program.referenceUrl && (
              <p className="text-sm mt-3 text-amber-900">
                Reference:
                {' '}
                <a
                  href={program.referenceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium hover:text-amber-950"
                >
                  {program.referenceUrl}
                </a>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BetaModeBanner;
